import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { employeeAPI } from '../../services/api';
import { 
  Shield, CheckCircle, Circle, Loader2, ChevronDown, ChevronUp,
  BadgeCheck, Star, Award, Crown, Sparkles
} from 'lucide-react';

// New tier system uses string values
const TIER_ICONS = {
  NONE: Circle,
  BASIC: Shield,
  VERIFIED: BadgeCheck,
  DISTINGUISHED: Award,
  ELITE: Crown
};

const TIER_COLORS = {
  NONE: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', solid: 'bg-gray-500' },
  BASIC: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', solid: 'bg-blue-500' },
  VERIFIED: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', solid: 'bg-green-500' },
  DISTINGUISHED: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', solid: 'bg-purple-500' },
  ELITE: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', solid: 'bg-amber-500' }
};

const TIER_ORDER = ['NONE', 'BASIC', 'VERIFIED', 'DISTINGUISHED', 'ELITE'];

const VettTierCard = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [tierData, setTierData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchTier();
  }, []);

  const fetchTier = async () => {
    try {
      const res = await employeeAPI.getVettTier(); 
      setTierData(res.data);
    } catch (error) {
      console.error('Error fetching tier:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#1A1A2E] border border-[#2A2A4A] rounded-xl p-4 animate-pulse">
        <div className="h-20 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#8888AA]" />
        </div>
      </Card>
    );
  }

  if (!tierData) return null;

  const { 
    tier = 'NONE', 
    tier_name = 'None', 
    criteria_met = [], 
    criteria_needed = [], 
    next_tier,
    next_tier_name,
    next_tier_requirements = [],
    metrics = {}
  } = tierData;
  
  const TierIcon = TIER_ICONS[tier] || Circle;
  const colors = TIER_COLORS[tier] || TIER_COLORS.NONE;
  const tierIndex = TIER_ORDER.indexOf(tier);

  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg} ${colors.border} border`}
        data-testid="tier-badge-compact"
      >
        <TierIcon className={`w-4 h-4 ${colors.text}`} />
        <span className={`text-sm font-medium ${colors.text}`}>{tier_name}</span>
      </div>
    );
  }

  return (
    <Card className="bg-[#1A1A2E] border border-[#2A2A4A] rounded-xl p-4" data-testid="vett-tier-card">
      {/* Tier Status Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
            <TierIcon className={`w-6 h-6 ${colors.text}`} />
          </div>
          <div>
            <p className="text-[#8888AA] text-xs uppercase tracking-wider">VETT Tier</p>
            <p className={`text-xl font-bold ${colors.text}`} data-testid="tier-name">
              {tier_name}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
          className="h-8 w-8 p-0 text-[#8888AA] hover:text-white"
          data-testid="tier-details-toggle"
        >
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>

      {/* Tier Progress Indicator */}
      <div className="flex items-center gap-1 mb-4">
        {TIER_ORDER.slice(1).map((t, i) => {
          const isActive = TIER_ORDER.indexOf(tier) > i;
          const tierColor = TIER_COLORS[t];
          return (
            <div
              key={t}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                isActive ? tierColor.solid : 'bg-[#2A2A4A]'
              }`}
            />
          );
        })}
      </div>

      {/* Key Metrics */}
      {metrics && Object.keys(metrics).length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-[#0D0D1A] rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-white">{metrics.verified_count || 0}</p>
            <p className="text-[#8888AA] text-xs">Verified</p>
          </div>
          <div className="bg-[#0D0D1A] rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-white">{metrics.avg_rating || 0}</p>
            <p className="text-[#8888AA] text-xs">Avg Rating</p>
          </div>
          <div className="bg-[#0D0D1A] rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-white">{metrics.employer_count || 0}</p>
            <p className="text-[#8888AA] text-xs">Employers</p>
          </div>
        </div>
      )}

      {/* Next Tier Requirements */}
      {next_tier && next_tier_requirements.length > 0 && (
        <div className="bg-[#0D0D1A] rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#E94560]" />
            <p className="text-[#8888AA] text-xs">
              To reach <span className={`font-medium ${TIER_COLORS[next_tier]?.text || 'text-white'}`}>{next_tier_name}</span>:
            </p>
          </div>
          <div className="space-y-1.5">
            {next_tier_requirements.slice(0, 3).map((req, i) => (
              <div key={i} className="flex items-start gap-2">
                <Circle className="w-3 h-3 text-[#E94560] mt-0.5 flex-shrink-0" />
                <span className="text-[#8888AA] text-xs">{req}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expanded Details */}
      {showDetails && (
        <div className="space-y-4 pt-4 border-t border-[#2A2A4A] animate-fade-in">
          {/* Criteria Met */}
          {criteria_met.length > 0 && (
            <div>
              <p className="text-green-400 text-xs font-medium mb-2">Criteria Met</p>
              <div className="space-y-1.5">
                {criteria_met.map((c, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white text-xs">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Criteria Needed */}
          {criteria_needed.length > 0 && (
            <div>
              <p className="text-amber-400 text-xs font-medium mb-2">Still Needed</p>
              <div className="space-y-1.5">
                {criteria_needed.map((c, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Circle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[#8888AA] text-xs">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tier Descriptions */}
          <div className="mt-4 pt-4 border-t border-[#2A2A4A]">
            <p className="text-[#8888AA] text-xs mb-2">VETT Tier Levels (0-100 Score)</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tierIndex >= 4 ? 'bg-amber-400' : 'bg-[#2A2A4A]'}`} />
                <span className={`text-xs ${tierIndex >= 4 ? 'text-amber-400' : 'text-[#8888AA]'}`}>
                  Distinguished (75-100): Comprehensive verification history
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tierIndex >= 3 ? 'bg-purple-400' : 'bg-[#2A2A4A]'}`} />
                <span className={`text-xs ${tierIndex >= 3 ? 'text-purple-400' : 'text-[#8888AA]'}`}>
                  Established (50-74): Multiple verified records across employers
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tierIndex >= 2 ? 'bg-green-400' : 'bg-[#2A2A4A]'}`} />
                <span className={`text-xs ${tierIndex >= 2 ? 'text-green-400' : 'text-[#8888AA]'}`}>
                  Verified (20-49): Identity confirmed, initial records verified
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tierIndex >= 1 ? 'bg-blue-400' : 'bg-[#2A2A4A]'}`} />
                <span className={`text-xs ${tierIndex >= 1 ? 'text-blue-400' : 'text-[#8888AA]'}`}>
                  Unverified (0-19): Profile created, verification in progress
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// Export a standalone TierBadge component for use in search results and profiles
export const TierBadge = ({ tier, tierName, size = 'sm' }) => {
  const TierIcon = TIER_ICONS[tier] || Circle;
  const colors = TIER_COLORS[tier] || TIER_COLORS.NONE;
  
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs gap-1',
    sm: 'px-2.5 py-1 text-xs gap-1.5',
    md: 'px-3 py-1.5 text-sm gap-2'
  };
  
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4'
  };

  return (
    <div 
      className={`inline-flex items-center rounded-full ${colors.bg} ${colors.border} border ${sizeClasses[size]}`}
      data-testid="tier-badge"
    >
      <TierIcon className={`${iconSizes[size]} ${colors.text}`} />
      <span className={`font-medium ${colors.text}`}>{tierName || tier}</span>
    </div>
  );
};

export default VettTierCard;
